import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from './auth';

const SITE_URL = 'https://szybkieprawko.online';
const SITE_NAME = 'Szybkie Prawko';

type FaqItem = {
  question: string;
  answer: string;
};

type SeoArticle = {
  slug: string;
  title: string;
  metaTitle: string;
  description: string;
  eyebrow: string;
  intro: string;
  primaryKeyword: string;
  ctaLabel: string;
  sections: Array<{
    heading: string;
    body: string[];
  }>;
  faq: FaqItem[];
};

type SeoMeta = {
  title: string;
  description: string;
  path: string;
  schema?: object | object[];
};

export const seoArticles: Record<string, SeoArticle> = {
  'egzamin-na-prawo-jazdy-kategoria-b': {
    slug: 'egzamin-na-prawo-jazdy-kategoria-b',
    title: 'Egzamin na prawo jazdy kategorii B - przebieg, koszty i zasady',
    metaTitle: 'Egzamin na prawo jazdy kat. B - jak wygląda krok po kroku?',
    description:
      'Sprawdź, jak wygląda egzamin na prawo jazdy kategorii B: PKK, kurs, teoria, praktyka, plac manewrowy, jazda w ruchu drogowym i koszty zapisu.',
    eyebrow: 'Kategoria B krok po kroku',
    intro:
      'Egzamin na prawo jazdy kategorii B składa się z kilku etapów: przygotowania dokumentów, kursu, egzaminu teoretycznego oraz egzaminu praktycznego. Poniżej znajdziesz konkretny opis procesu od założenia PKK aż po odbiór dokumentu prawa jazdy.',
    primaryKeyword: 'egzamin na prawo jazdy kat. B',
    ctaLabel: 'Przygotuj się do teorii kat. B',
    sections: [
      {
        heading: '1. Zanim zapiszesz się na egzamin: PKK, badania i kurs',
        body: [
          'Pierwszym formalnym krokiem jest uzyskanie numeru PKK, czyli profilu kandydata na kierowcę. Do jego założenia potrzebujesz między innymi badania lekarskiego, zdjęcia oraz dokumentów składanych w wydziale komunikacji właściwym dla miejsca zamieszkania.',
          'Kurs kategorii B obejmuje część teoretyczną i praktyczną. Standardowo kandydat realizuje 30 godzin teorii oraz 30 godzin jazd praktycznych. Dopiero po ukończeniu szkolenia i spełnieniu wymagań możesz zapisać się na państwowy egzamin.',
        ],
      },
      {
        heading: '2. Egzamin teoretyczny kat. B',
        body: [
          'Część teoretyczna odbywa się w sali komputerowej w WORD. Test trwa 25 minut i składa się z 32 pytań: 20 pytań z wiedzy podstawowej oraz 12 pytań specjalistycznych dla kategorii B.',
          'Pytania mają różną wagę punktową. Maksymalny wynik to 74 punkty, a do zdania trzeba uzyskać co najmniej 68 punktów. W części podstawowej odpowiadasz TAK albo NIE, natomiast w części specjalistycznej wybierasz jedną z odpowiedzi A, B lub C.',
          'Ważne: w egzaminie teoretycznym nie warto liczyć na cofanie się do poprzednich pytań. Dlatego podczas nauki dobrze ćwiczyć nie tylko znajomość odpowiedzi, ale też tempo pracy i skupienie.',
        ],
      },
      {
        heading: '3. Egzamin praktyczny kat. B: plac manewrowy',
        body: [
          'Po zdaniu teorii możesz podejść do części praktycznej. Egzamin praktyczny zaczyna się od czynności kontrolno-obsługowych i przygotowania do jazdy. Kandydat może zostać poproszony o sprawdzenie wybranych elementów pojazdu, na przykład świateł albo płynów eksploatacyjnych.',
          'Następnie przygotowujesz auto do jazdy: ustawiasz fotel, zagłówek, lusterka, zapinasz pasy i upewniasz się, że pojazd jest gotowy do ruszenia. Na placu manewrowym wykonuje się między innymi jazdę pasem ruchu do przodu i do tyłu oraz ruszanie na wzniesieniu.',
        ],
      },
      {
        heading: '4. Egzamin praktyczny kat. B: jazda w ruchu drogowym',
        body: [
          'Po zaliczeniu zadań na placu wyjeżdżasz z egzaminatorem na drogi publiczne. Egzaminator ocenia sposób wykonywania manewrów, obserwację drogi, stosowanie się do znaków, zachowanie wobec pieszych i innych kierujących oraz umiejętność reagowania na zagrożenia.',
          'Praktyka trwa zwykle około 40 minut, ale może zakończyć się szybciej, jeżeli wszystkie wymagane zadania zostały wykonane poprawnie. Po egzaminie egzaminator omawia wynik i wskazuje ewentualne przyczyny niezaliczenia.',
        ],
      },
      {
        heading: '5. Ile kosztuje egzamin na prawo jazdy kat. B?',
        body: [
          'Za egzamin teoretyczny i praktyczny płaci się osobno. Według aktualnych stawek opisywanych w poradnikach branżowych teoria kosztuje 55 zł, a praktyka dla kategorii B może kosztować 222 zł. W praktyce kwoty warto sprawdzić bezpośrednio w lokalnym WORD lub systemie zapisów, bo opłaty wynikają z aktualnych cenników i przepisów regionalnych.',
          'Do całkowitego kosztu uzyskania prawa jazdy dochodzą też inne wydatki: badanie lekarskie, zdjęcie, kurs w szkole jazdy, ewentualne jazdy doszkalające oraz opłata za wydanie dokumentu po zdanym egzaminie.',
        ],
      },
      {
        heading: '6. Co po zdanym egzaminie?',
        body: [
          'Po pozytywnym wyniku dane z egzaminu trafiają do systemu i uzupełniają Twój profil kandydata na kierowcę. Następnie trzeba wnieść opłatę za wydanie prawa jazdy i czekać na przygotowanie dokumentu.',
          'Do czasu uzyskania uprawnień nie należy prowadzić samochodu samodzielnie. Sam fakt zdania egzaminu nie oznacza jeszcze, że możesz legalnie kierować pojazdem bez spełnienia formalności dotyczących dokumentu.',
        ],
      },
    ],
    faq: [
      {
        question: 'Ile pytań jest na egzaminie teoretycznym kat. B?',
        answer:
          'Egzamin teoretyczny kategorii B ma 32 pytania: 20 z wiedzy podstawowej i 12 z wiedzy specjalistycznej.',
      },
      {
        question: 'Ile punktów trzeba zdobyć, żeby zdać teorię kat. B?',
        answer:
          'Maksymalnie można zdobyć 74 punkty, a wynik pozytywny wymaga co najmniej 68 punktów.',
      },
      {
        question: 'Ile trwa egzamin teoretyczny kat. B?',
        answer:
          'Część teoretyczna trwa 25 minut. W tym czasie trzeba odpowiedzieć na wszystkie pytania testowe.',
      },
      {
        question: 'Z czego składa się egzamin praktyczny kat. B?',
        answer:
          'Praktyka obejmuje czynności kontrolno-obsługowe, przygotowanie do jazdy, zadania na placu manewrowym oraz jazdę w ruchu drogowym.',
      },
      {
        question: 'Ile kosztuje egzamin kat. B?',
        answer:
          'Teoria jest opłacana osobno od praktyki. Często podawane stawki to 55 zł za teorię i 222 zł za praktykę kat. B, ale przed zapisem sprawdź aktualny cennik swojego WORD.',
      },
    ],
  },
  'testy-na-prawo-jazdy': {
    slug: 'testy-na-prawo-jazdy',
    title: 'Testy na prawo jazdy - jak skutecznie przygotować się do teorii?',
    metaTitle: 'Testy na prawo jazdy 2026 - pytania i egzamin próbny online',
    description:
      'Rozwiązuj testy na prawo jazdy online, ćwicz pytania egzaminacyjne i sprawdź, jak przygotować się do teorii w WORD krok po kroku.',
    eyebrow: 'Egzamin teoretyczny bez chaosu',
    intro:
      'Testy na prawo jazdy pomagają szybko sprawdzić, czy znasz przepisy, znaki, pierwszeństwo przejazdu i zasady bezpieczeństwa. W Szybkim Prawku możesz ćwiczyć pytania w trybie nauki, a potem przejść do egzaminu próbnego z punktacją podobną do tej, którą znasz z WORD.',
    primaryKeyword: 'testy na prawo jazdy',
    ctaLabel: 'Rozpocznij testy na prawo jazdy',
    sections: [
      {
        heading: 'Dlaczego regularne testy na prawo jazdy są skuteczniejsze niż sama teoria?',
        body: [
          'Sama lektura przepisów rzadko wystarcza, bo egzamin teoretyczny wymaga szybkiego rozpoznawania sytuacji drogowych. Testy na prawo jazdy uczą podejmowania decyzji pod presją czasu i pokazują, które tematy wymagają powtórki.',
          'Najlepszy efekt daje połączenie krótkich sesji nauki z pełnymi egzaminami próbnymi. Najpierw ćwiczysz konkretne zagadnienia, a później sprawdzasz, czy potrafisz utrzymać wynik w kompletnym teście.',
        ],
      },
      {
        heading: 'Jak korzystać z testów, żeby realnie zwiększyć szansę zdania?',
        body: [
          'Zacznij od trybu nauki i nie pomijaj błędnych odpowiedzi. Jeżeli pytanie sprawia problem, wróć do niego po kilku godzinach albo następnego dnia. Taki powrót utrwala materiał lepiej niż jednorazowe przejście przez dużą liczbę pytań.',
          'Gdy uzyskujesz stabilne wyniki, uruchom egzamin próbny. Traktuj go jak symulację: nie zatrzymuj testu, nie sprawdzaj odpowiedzi w trakcie i zwracaj uwagę na limit czasu.',
        ],
      },
      {
        heading: 'Kategorie prawa jazdy i zakres pytań',
        body: [
          'W bazie znajdziesz pytania dla popularnych kategorii, w tym kategorii B, ale też materiał dla motocykli, pojazdów ciężarowych, autobusów i innych uprawnień. Dzięki temu jedno konto może służyć do nauki różnych zakresów egzaminu.',
          'Pytania podstawowe sprawdzają ogólne zasady ruchu drogowego, a specjalistyczne dotyczą wybranej kategorii. Warto ćwiczyć oba zakresy, ponieważ wynik końcowy zależy od całości testu.',
        ],
      },
    ],
    faq: [
      {
        question: 'Czy testy na prawo jazdy online wystarczą do przygotowania do egzaminu?',
        answer:
          'Są bardzo ważnym elementem przygotowania, ale najlepszy efekt daje połączenie testów z analizą błędów i powtarzaniem przepisów, które sprawiają trudność.',
      },
      {
        question: 'Ile razy dziennie warto rozwiązywać testy na prawo jazdy?',
        answer:
          'Lepiej ćwiczyć regularnie po 15-30 minut niż robić jedną bardzo długą sesję. Krótsze powtórki ułatwiają zapamiętywanie pytań i zasad.',
      },
      {
        question: 'Czy mogę ćwiczyć pytania z różnych kategorii?',
        answer:
          'Tak, możesz wybrać kategorię egzaminu i dopasować zakres nauki do uprawnień, o które się starasz.',
      },
      {
        question: 'Czy platforma Szybkie Prawko jest darmowa?',
        answer:
          'Tak. Z platformy możesz korzystać całkowicie za darmo, uczyć się do egzaminu i rozwiązywać testy na prawo jazdy online.',
      },
      {
        question: 'Co daje Premium?',
        answer:
          'Premium usuwa reklamy i odblokowuje dostęp do rozszerzonych statystyk, dzięki którym łatwiej analizować postęp nauki oraz słabsze obszary.',
      },
      {
        question: 'Jak wygląda egzamin na prawo jazdy?',
        answer:
          'Egzamin składa się z części teoretycznej i praktycznej. Teoria to test komputerowy z pytaniami podstawowymi i specjalistycznymi, a praktyka obejmuje zadania na placu manewrowym oraz jazdę w ruchu drogowym.',
      },
      {
        question: 'Ile kosztuje zapisanie się na egzamin na prawo jazdy?',
        answer:
          'Opłatę wnosi się osobno za część teoretyczną i praktyczną. Wysokość zależy od kategorii i aktualnego cennika WORD, dlatego przed zapisem najlepiej sprawdzić kwotę w lokalnym WORD albo w systemie rezerwacji egzaminu.',
      },
    ],
  },
  'darmowe-testy-na-prawo-jazdy': {
    slug: 'darmowe-testy-na-prawo-jazdy',
    title: 'Darmowe testy na prawo jazdy - sprawdź pytania przed egzaminem',
    metaTitle: 'Darmowe testy na prawo jazdy online - przykładowe pytania',
    description:
      'Skorzystaj z darmowych testów na prawo jazdy, zobacz przykładowe pytania i sprawdź, czy jesteś gotowy do nauki pełnego zakresu.',
    eyebrow: 'Start bez płatności',
    intro:
      'Darmowe testy na prawo jazdy to dobry sposób, żeby sprawdzić poziom wiedzy przed rozpoczęciem pełnej nauki. Możesz zobaczyć przykładowe pytania, poznać sposób działania aplikacji i zdecydować, które tematy wymagają najwięcej pracy.',
    primaryKeyword: 'darmowe testy na prawo jazdy',
    ctaLabel: 'Zobacz darmowe pytania',
    sections: [
      {
        heading: 'Co dają darmowe testy na prawo jazdy?',
        body: [
          'Darmowy podgląd pomaga ocenić, jak wyglądają pytania egzaminacyjne i czy umiesz szybko wybrać poprawną odpowiedź. To szczególnie przydatne na początku nauki, kiedy nie wiesz jeszcze, które działy są najtrudniejsze.',
          'Bezpłatne pytania nie zastępują pełnego przygotowania, ale pozwalają rozpocząć bez ryzyka. Po kilku przykładach łatwiej zaplanować regularną naukę i przejść do pełnego trybu testów.',
        ],
      },
      {
        heading: 'Jak wykorzystać darmową naukę przed zakupem pełnego dostępu?',
        body: [
          'Najpierw rozwiąż przykładowe pytania bez podpowiedzi. Następnie sprawdź błędy i zapisz tematy, które się powtarzają: znaki, pierwszeństwo, prędkość, zachowanie wobec pieszych albo podstawy techniki jazdy.',
          'Jeżeli widzisz, że problem dotyczy wielu działów, przejdź do systematycznego trybu nauki. Pełna baza pytań jest wtedy bardziej opłacalna, bo pozwala trenować cały materiał, a nie tylko pojedyncze przykłady.',
        ],
      },
      {
        heading: 'Darmowe testy a egzamin próbny',
        body: [
          'Darmowe pytania służą do szybkiego startu, natomiast egzamin próbny pokazuje gotowość do realnego sprawdzianu. Warto korzystać z obu form, ale w innym momencie przygotowań.',
          'Na początku najważniejsze jest zrozumienie pytań. Pod koniec nauki liczy się stabilny wynik, tempo odpowiedzi i odporność na presję czasu.',
        ],
      },
    ],
    faq: [
      {
        question: 'Czy darmowe testy na prawo jazdy są dostępne bez konta?',
        answer:
          'Na stronie możesz zobaczyć przykładowe pytania. Konto pozwala wygodniej zapisywać postęp i przejść do dalszej nauki.',
      },
      {
        question: 'Czy darmowe pytania wystarczą do zdania egzaminu?',
        answer:
          'Darmowy zakres jest dobry na start, ale do pełnego przygotowania warto ćwiczyć szerszą bazę pytań i regularnie robić egzaminy próbne.',
      },
      {
        question: 'Kiedy przejść z darmowego podglądu do pełnej nauki?',
        answer:
          'Gdy chcesz ćwiczyć systematycznie, zapisywać wyniki i przerobić pytania z pełnego zakresu wybranej kategorii.',
      },
      {
        question: 'Czy platforma jest całkowicie darmowa?',
        answer:
          'Tak. Możesz korzystać z platformy za darmo. Premium jest opcjonalne i służy głównie do nauki bez reklam oraz korzystania z rozszerzonych statystyk.',
      },
      {
        question: 'Z czego składa się państwowy egzamin na prawo jazdy?',
        answer:
          'Najpierw zdaje się egzamin teoretyczny, czyli test komputerowy. Po zdanej teorii można podejść do egzaminu praktycznego, który sprawdza przygotowanie do jazdy i zachowanie w realnym ruchu drogowym.',
      },
    ],
  },
  'testy-na-prawo-jazdy-online': {
    slug: 'testy-na-prawo-jazdy-online',
    title: 'Testy na prawo jazdy online - ucz się wygodnie na komputerze i telefonie',
    metaTitle: 'Testy na prawo jazdy online - egzamin próbny i tryb nauki',
    description:
      'Ćwicz testy na prawo jazdy online, rozwiązuj pytania z mediami i sprawdzaj wynik w egzaminie próbnym z dowolnego miejsca.',
    eyebrow: 'Nauka tam, gdzie masz czas',
    intro:
      'Testy na prawo jazdy online pozwalają uczyć się wtedy, gdy masz wolną chwilę: w domu, w przerwie albo przed zajęciami. Nie musisz instalować dodatkowego programu - wystarczy przeglądarka i konto, które zapisuje postęp nauki.',
    primaryKeyword: 'testy na prawo jazdy online',
    ctaLabel: 'Ćwicz testy online',
    sections: [
      {
        heading: 'Dlaczego testy online są wygodne dla kursantów?',
        body: [
          'Nauka online skraca dystans między teorią a praktyką. Możesz rozwiązać kilka pytań w krótkiej sesji, a później wrócić do miejsca, w którym przerwałeś. To ułatwia regularność, która ma duże znaczenie przed egzaminem.',
          'Dostęp przez przeglądarkę sprawia, że nie jesteś przywiązany do jednego urządzenia. Jeżeli korzystasz z telefonu, możesz ćwiczyć krótkie serie, a na komputerze wygodnie przechodzić pełne egzaminy próbne.',
        ],
      },
      {
        heading: 'Tryb nauki online i egzamin próbny',
        body: [
          'Tryb nauki jest najlepszy do przerabiania materiału etapami. Pozwala skupić się na pytaniach, odpowiedziach i błędach bez presji końcowego wyniku.',
          'Egzamin próbny online warto uruchomić wtedy, gdy znasz już większość materiału. Wynik pokazuje, czy utrzymujesz wymagany poziom i czy potrafisz odpowiadać w określonym czasie.',
        ],
      },
      {
        heading: 'Jak trenować online bez marnowania czasu?',
        body: [
          'Ustal prosty rytm: codziennie krótka nauka, co kilka dni egzamin próbny i osobna powtórka błędnych odpowiedzi. Dzięki temu nie tylko rozpoznajesz poprawne warianty, ale też rozumiesz, dlaczego pozostałe są błędne.',
          'Nie warto klikać pytań automatycznie. Jeżeli nie znasz odpowiedzi, zatrzymaj się i przeanalizuj sytuację drogową. To lepiej przygotowuje do realnego egzaminu.',
        ],
      },
    ],
    faq: [
      {
        question: 'Czy testy na prawo jazdy online działają na telefonie?',
        answer:
          'Tak, strona działa w przeglądarce, więc możesz ćwiczyć na telefonie, tablecie albo komputerze.',
      },
      {
        question: 'Czy postęp nauki online jest zapisywany?',
        answer:
          'Po zalogowaniu konto może przechowywać ustawienia, preferencje i wyniki, dzięki czemu łatwiej wracać do nauki.',
      },
      {
        question: 'Czy egzamin próbny online ma limit czasu?',
        answer:
          'Tak, egzamin próbny jest przygotowany jako symulacja sprawdzianu teoretycznego, więc warto traktować go jak realny test.',
      },
      {
        question: 'Czy testy online są darmowe?',
        answer:
          'Tak. Platforma jest całkowicie darmowa. Premium jest dobrowolne i daje brak reklam oraz rozszerzone statystyki nauki.',
      },
      {
        question: 'Jak wygląda egzamin teoretyczny na prawo jazdy?',
        answer:
          'Egzamin teoretyczny odbywa się przy komputerze. Składa się z pytań podstawowych oraz specjalistycznych dla wybranej kategorii, a odpowiedzi udziela się w ograniczonym czasie.',
      },
    ],
  },
  'nauka-na-prawo-jazdy': {
    slug: 'nauka-na-prawo-jazdy',
    title: 'Nauka na prawo jazdy - plan przygotowania do egzaminu teoretycznego',
    metaTitle: 'Nauka na prawo jazdy online - pytania, testy i egzamin próbny',
    description:
      'Poznaj prosty plan nauki na prawo jazdy: teoria, pytania egzaminacyjne, powtórki błędów i egzaminy próbne online.',
    eyebrow: 'Plan nauki krok po kroku',
    intro:
      'Nauka na prawo jazdy jest łatwiejsza, gdy masz konkretny plan. Zamiast losowo klikać pytania, warto połączyć teorię, regularne powtórki i egzaminy próbne, które pokazują gotowość do WORD.',
    primaryKeyword: 'nauka na prawo jazdy',
    ctaLabel: 'Zacznij naukę na prawo jazdy',
    sections: [
      {
        heading: 'Od czego zacząć naukę na prawo jazdy?',
        body: [
          'Najpierw poznaj podstawowe zasady ruchu drogowego: znaki, pierwszeństwo, ograniczenia prędkości, zachowanie wobec pieszych i zasady bezpieczeństwa. To fundament, który pomaga rozumieć pytania egzaminacyjne, a nie tylko zapamiętywać odpowiedzi.',
          'Następnie przejdź do krótkich serii pytań. Po każdej sesji sprawdzaj błędy i zapisuj tematy do powtórki. Taki system jest skuteczniejszy niż jednorazowe rozwiązywanie setek pytań.',
        ],
      },
      {
        heading: 'Przykładowy plan nauki przed egzaminem teoretycznym',
        body: [
          'Pierwsze dni przeznacz na poznanie zasad i spokojne rozwiązywanie pytań. W kolejnym etapie zwiększ liczbę krótkich sesji i wracaj do błędów. Ostatni etap to egzaminy próbne wykonywane w warunkach podobnych do realnego testu.',
          'Jeżeli wynik egzaminu próbnego jest niestabilny, nie przyspieszaj. Wróć do tematów, które obniżają punktację, i dopiero potem wykonaj kolejną symulację.',
        ],
      },
      {
        heading: 'Najczęstsze błędy podczas nauki teorii',
        body: [
          'Najczęstszy błąd to uczenie się odpowiedzi bez rozumienia sytuacji. Na egzaminie podobne pytania mogą wyglądać inaczej, dlatego ważne jest rozpoznawanie zasady, a nie tylko konkretnego obrazka.',
          'Drugim problemem jest brak powtórek. Jeżeli nie wracasz do błędnych pytań, łatwo uzyskać pozornie dobry wynik, który nie powtarza się w pełnym egzaminie próbnym.',
        ],
      },
    ],
    faq: [
      {
        question: 'Ile trwa nauka na prawo jazdy do teorii?',
        answer:
          'To zależy od regularności i punktu startowego. W praktyce lepiej uczyć się codziennie krócej niż odkładać wszystko na ostatnie dni przed egzaminem.',
      },
      {
        question: 'Czy lepiej uczyć się działami czy losowymi pytaniami?',
        answer:
          'Na początku warto uczyć się działami, a później przejść do losowych zestawów i egzaminów próbnych.',
      },
      {
        question: 'Kiedy jestem gotowy do egzaminu teoretycznego?',
        answer:
          'Dobrym sygnałem jest regularne uzyskiwanie wyniku powyżej progu zdawalności w egzaminach próbnych i coraz mniejsza liczba powtarzających się błędów.',
      },
      {
        question: 'Czy Premium jest wymagane do nauki?',
        answer:
          'Nie. Nauka na platformie jest darmowa. Premium jest opcjonalne i daje wygodniejszą naukę bez reklam oraz dostęp do rozszerzonych statystyk.',
      },
      {
        question: 'Co trzeba zrobić przed zapisaniem się na egzamin?',
        answer:
          'Najpierw trzeba mieć profil kandydata na kierowcę, ukończyć wymagane szkolenie i wybrać termin w WORD. Przy zapisie płaci się za wybraną część egzaminu, a aktualną stawkę warto sprawdzić w lokalnym ośrodku.',
      },
    ],
  },
};

function setMetaTag(selector: string, attr: 'content' | 'href', value: string) {
  const element = document.head.querySelector(selector);
  if (element) {
    element.setAttribute(attr, value);
  }
}

function createFaqSchema(article: SeoArticle) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

function createArticleSchema(article: SeoArticle) {
  const url = `${SITE_URL}/${article.slug}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    inLanguage: 'pl-PL',
    mainEntityOfPage: url,
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
  };
}

function createBreadcrumbSchema(article: SeoArticle) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Strona główna',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: `${SITE_URL}/${article.slug}`,
      },
    ],
  };
}

export function useSeo({ title, description, path, schema }: SeoMeta) {
  useEffect(() => {
    const canonical = `${SITE_URL}${path}`;
    document.title = title;
    setMetaTag('meta[name="description"]', 'content', description);
    setMetaTag('link[rel="canonical"]', 'href', canonical);
    setMetaTag('meta[property="og:title"]', 'content', title);
    setMetaTag('meta[property="og:description"]', 'content', description);
    setMetaTag('meta[property="og:url"]', 'content', canonical);
    setMetaTag('meta[name="twitter:title"]', 'content', title);
    setMetaTag('meta[name="twitter:description"]', 'content', description);

    const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];
    document.querySelectorAll('script[data-dynamic-schema="true"]').forEach((node) => node.remove());
    schemas.forEach((schemaItem, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.dynamicSchema = 'true';
      script.dataset.schemaIndex = String(index);
      script.text = JSON.stringify(schemaItem);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-dynamic-schema="true"]').forEach((node) => node.remove());
    };
  }, [description, path, schema, title]);
}

function FaqAccordion({ items }: { items: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="seo-faq__grid">
      {items.map((item, index) => {
        const isOpen = openIndex === index;

        return (
          <article className={`seo-faq__item ${isOpen ? 'seo-faq__item--open' : ''}`} key={item.question}>
            <button
              aria-expanded={isOpen}
              className="seo-faq__question"
              onClick={() => setOpenIndex(isOpen ? -1 : index)}
              type="button"
            >
              <span>{item.question}</span>
              <span className="seo-faq__icon" aria-hidden="true">
                {isOpen ? '-' : '+'}
              </span>
            </button>
            {isOpen ? <p className="seo-faq__answer">{item.answer}</p> : null}
          </article>
        );
      })}
    </div>
  );
}

export function SeoLandingPage({ slug }: { slug: string }) {
  const article = seoArticles[slug];
  const { isAuthenticated } = useAuth();

  if (!article) {
    return null;
  }

  useSeo({
    title: article.metaTitle,
    description: article.description,
    path: `/${article.slug}`,
    schema: [createArticleSchema(article), createFaqSchema(article), createBreadcrumbSchema(article)],
  });

  return (
    <main className="seo-page">
      <section className="seo-hero">
        <div>
          <Link className="seo-page__breadcrumb" to="/">
            Strona główna
          </Link>
          <span className="seo-page__eyebrow">{article.eyebrow}</span>
          <h1>{article.title}</h1>
          <p>{article.intro}</p>
          <div className="seo-page__actions">
            <Link className="landing-button landing-button--primary" to={isAuthenticated ? '/study' : '/login'}>
              {article.ctaLabel}
            </Link>
            <Link className="landing-button landing-button--secondary" to="/question-demo">
              Zobacz przykładowe pytanie
            </Link>
          </div>
        </div>
        <aside className="seo-hero__card" aria-label="Najważniejsze informacje">
          <strong>{article.primaryKeyword}</strong>
          <span>Tryb nauki, przykładowe pytania i egzamin próbny w jednym miejscu.</span>
        </aside>
      </section>

      <article className="seo-article">
        {article.sections.map((section) => (
          <section key={section.heading}>
            <h2>{section.heading}</h2>
            {section.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </section>
        ))}
      </article>

      <section className="seo-faq" aria-labelledby="seo-faq-title">
        <h2 id="seo-faq-title">FAQ - najczęstsze pytania</h2>
        <FaqAccordion items={article.faq} />
      </section>

      <section className="seo-related" aria-labelledby="seo-related-title">
        <h2 id="seo-related-title">Powiązane tematy</h2>
        <div>
          {Object.values(seoArticles)
            .filter((related) => related.slug !== article.slug)
            .map((related) => (
              <Link key={related.slug} to={`/${related.slug}`}>
                {related.primaryKeyword}
              </Link>
            ))}
        </div>
      </section>
    </main>
  );
}

export function HomeSeoSections() {
  const { isAuthenticated } = useAuth();

  return (
    <>
      <section className="landing-seo-intro" aria-labelledby="landing-seo-title">
        <div>
          <span className="landing-hero__eyebrow">Testy, nauka i egzamin próbny</span>
          <h2 id="landing-seo-title">Testy na prawo jazdy online dla osób przygotowujących się do teorii</h2>
          <p>
            Szybkie Prawko pomaga uczyć się do egzaminu teoretycznego przez regularne testy na prawo jazdy, darmowy podgląd pytań i pełne egzaminy próbne. Możesz zacząć od krótkiej sesji nauki, a później sprawdzić wynik w warunkach zbliżonych do prawdziwego testu.
          </p>
        </div>
        <Link className="landing-button landing-button--primary" to={isAuthenticated ? '/study' : '/login'}>
          Zacznij naukę online
        </Link>
      </section>

      <section className="landing-topic-grid" aria-label="Tematy nauki na prawo jazdy">
        {Object.values(seoArticles).map((article) => (
          <article key={article.slug}>
            <span>{article.primaryKeyword}</span>
            <h2>{article.title.replace(' - ', ': ')}</h2>
            <p>{article.description}</p>
            <Link to={`/${article.slug}`}>Czytaj więcej</Link>
          </article>
        ))}
      </section>

      <section className="landing-faq" aria-labelledby="landing-faq-title">
        <h2 id="landing-faq-title">Najczęstsze pytania o testy na prawo jazdy</h2>
        <FaqAccordion
          items={[
            {
              question: 'Czy mogę zacząć od darmowych testów na prawo jazdy?',
              answer:
                'Tak. Darmowy podgląd pytań pozwala sprawdzić sposób działania aplikacji i ocenić, od których tematów warto zacząć naukę.',
            },
            {
              question: 'Czy testy online pomagają przygotować się do egzaminu w WORD?',
              answer:
                'Tak, bo uczą rozpoznawania sytuacji drogowych, pracy z pytaniami i kontroli czasu. Najlepiej łączyć je z analizą błędów.',
            },
            {
              question: 'Jak często robić egzamin próbny?',
              answer:
                'Na początku wystarczy co kilka dni. Przed egzaminem warto robić go częściej i obserwować, czy wynik jest stabilny.',
            },
            {
              question: 'Czy platforma jest całkowicie darmowa?',
              answer:
                'Tak. Możesz korzystać z platformy całkowicie za darmo, rozwiązywać testy i uczyć się do egzaminu teoretycznego online.',
            },
            {
              question: 'Co daje konto Premium?',
              answer:
                'Premium usuwa reklamy i daje dostęp do rozszerzonych statystyk, które pomagają śledzić postęp oraz szybciej znaleźć tematy wymagające powtórki.',
            },
            {
              question: 'Jak wygląda egzamin na prawo jazdy?',
              answer:
                'Egzamin ma część teoretyczną i praktyczną. Teoria to test komputerowy z pytaniami podstawowymi oraz specjalistycznymi, a praktyka sprawdza manewry i jazdę w ruchu drogowym.',
            },
            {
              question: 'Ile kosztuje zapisanie się na egzamin?',
              answer:
                'Za teorię i praktykę płaci się osobno. Kwota zależy od kategorii i aktualnego cennika WORD, dlatego przed rezerwacją terminu najlepiej sprawdzić opłatę w swoim WORD albo w systemie zapisów.',
            },
          ]}
        />
      </section>
    </>
  );
}
